import React, { useState, useMemo, useCallback, useEffect } from 'react';
import remarkGfm from 'remark-gfm';
import { detectContentType, ContentTypes, extractSections, analyzeComplexity } from '../utils/contentDetection';
import { parseTable, splitAroundTable, tableToHTML } from '../utils/tableParser';
import SafeMarkdown from './SafeMarkdown';
import { useToast } from '../services/toastContext';
import VChartWrapper from './VChartWrapper';
import { MAX_CHART_DATA_POINTS } from './charts/limits';
import DocumentList from './documents/DocumentList';
import { getApiUrl } from '../services/runtimeConfig';

/**
 * Coerce a raw series value to a finite number.
 * Accepts numbers and numeric strings; rejects null, blank strings, and NaN so
 * unplottable points are dropped rather than silently rendered as zero.
 * @param {*} value Raw series value
 * @returns {number|null} Finite number, or null when not numeric
 */
const toFiniteChartValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Resolve a payload's chart type as the first non-blank of `chart_type`, `type`.
 *
 * Must stay in step with the backend's `_is_renderable_chart_payload`; if one
 * side commits to a blank `chart_type` while the other falls through to `type`,
 * the two disagree about renderability and a real chart is either suppressed
 * behind a failure notice or rendered twice.
 *
 * @param {Object} candidate Parsed chart payload
 * @returns {string|null} Trimmed type name, or null when neither is usable
 */
const resolveChartTypeName = (candidate) => {
  const name = [candidate.chart_type, candidate.type].find(
    (value) => typeof value === 'string' && value.trim()
  );
  return name ? name.trim() : null;
};

/**
 * Read the series list out of a wide-format data container, accepting both the
 * `series`/`name` and the Chart.js `datasets`/`label` spellings.
 * @param {Object} container The payload's `data` object
 * @returns {Array<Object>|null} Series entries, or null when absent or empty
 */
const extractWideSeriesList = (container) => {
  for (const key of ['series', 'datasets']) {
    if (Array.isArray(container[key]) && container[key].length > 0) {
      return container[key];
    }
  }
  return null;
};

/**
 * Convert a wide-format chart payload into the long-format spec the chart
 * factory consumes.
 *
 * Models frequently emit `{type, data: {labels, series}}` — a Chart.js-shaped
 * spec — instead of the `{chart_type, data: [{x, value, series}]}` shape the
 * backend chart tools produce. Chart tooling is shared across many agents, so
 * the renderer converts the wide shape rather than requiring every agent prompt
 * to restate the contract. Field names and options mirror the backend's
 * `_normalize_timeseries_line` so both producers converge on one spec shape.
 *
 * @param {Object} candidate Parsed payload whose `data` is a plain object
 * @returns {Object|null} Long-format chart spec, or null when not convertible
 */
const normalizeWideSeriesChartData = (candidate) => {
  const rawType = resolveChartTypeName(candidate);
  if (!rawType) return null;

  const container = candidate.data;
  if (!container || typeof container !== 'object' || Array.isArray(container)) return null;
  if (!Array.isArray(container.labels) || container.labels.length === 0) return null;

  const seriesList = extractWideSeriesList(container);
  if (!seriesList) return null;

  // Multi-series payloads get a name for every entry, including unnamed ones.
  // Chart.js datasets routinely omit `label`; leaving those rows without a
  // `series` key either collapses every dataset into one line (when no entry is
  // named) or groups the unnamed ones under `undefined`.
  const seriesNames = seriesList.map((entry, index) => {
    const rawName = entry && typeof entry === 'object' && !Array.isArray(entry)
      ? (typeof entry.name === 'string' ? entry.name : entry.label)
      : null;
    if (typeof rawName === 'string' && rawName.trim()) return rawName.trim();
    return seriesList.length > 1 ? `Series ${index + 1}` : null;
  });

  const rows = [];
  seriesList.forEach((entry, seriesIndex) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;

    const values = Array.isArray(entry.data)
      ? entry.data
      : (Array.isArray(entry.values) ? entry.values : null);
    if (!values) return;

    const seriesName = seriesNames[seriesIndex];

    container.labels.forEach((label, index) => {
      const value = toFiniteChartValue(values[index]);
      if (value === null) return;
      rows.push({
        x: String(label),
        value,
        ...(seriesName ? { series: seriesName } : {})
      });
    });
  });

  if (rows.length === 0) return null;
  // Conversion is multiplicative, so an ordinary-looking payload can outgrow the
  // factory's limit. Degrade to the failure notice rather than throwing inside
  // the factory, whose error branch renders an uncapped table.
  if (rows.length > MAX_CHART_DATA_POINTS) return null;

  const sourceOptions =
    candidate.options && typeof candidate.options === 'object' && !Array.isArray(candidate.options)
      ? candidate.options
      : {};
  const { title: optionsTitle, ...passthroughOptions } = sourceOptions;
  const xAxisOverride =
    passthroughOptions.xAxis
    && typeof passthroughOptions.xAxis === 'object'
    && !Array.isArray(passthroughOptions.xAxis)
      ? passthroughOptions.xAxis
      : {};

  const normalized = {
    chart_type: rawType.trim(),
    data: rows,
    options: {
      ...passthroughOptions,
      xField: 'x',
      yField: 'value',
      xAxis: { type: 'band', ...xAxisOverride }
    }
  };
  if (rows.some((row) => row.series)) {
    normalized.options.seriesField = 'series';
  }

  const title = [candidate.title, optionsTitle].find(
    (value) => typeof value === 'string' && value.trim()
  );
  if (title) {
    normalized.title = title.trim();
  }

  return normalized;
};

/**
 * Field names a chart payload may legitimately carry. Used only to describe a
 * rejected payload without echoing keys that may themselves be data. Mirrors
 * `_CHART_PAYLOAD_FIELDS` in `backend/api/services/chat_service.py`.
 */
const CHART_PAYLOAD_FIELDS = new Set([
  'chart_id', 'chartId', 'chart_type', 'type', 'data', 'options', 'title',
  'graph', 'chartSpec', 'chart_spec', 'labels', 'series', 'datasets',
  'nameField', 'valueField', 'labels_field', 'values_field', 'status'
]);

/**
 * Describe why a chart fence was rejected, without duplicating the gatekeeper's
 * shape logic — a parallel classifier would be one more thing to drift.
 * @param {string} [raw] Raw fence body
 * @returns {{reason: string, detail: string}} Reason code and diagnostic detail
 */
const describeChartRejection = (raw) => {
  const body = typeof raw === 'string' ? raw : '';
  let parsed;
  try {
    parsed = JSON.parse(body);
  } catch (err) {
    // `err.message` embeds a slice of the document, so only the error name is
    // reported — the reason code is the diagnostic signal, not the content.
    return { reason: 'invalid_json', detail: err.name };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { reason: 'unsupported_shape', detail: Array.isArray(parsed) ? 'array' : typeof parsed };
  }
  // Never report the payload's own keys: a map-shaped payload holds its data in
  // the keys, so only recognised chart fields and a count are reported.
  const recognised = Object.keys(parsed).filter((key) => CHART_PAYLOAD_FIELDS.has(key));
  return {
    reason: 'unsupported_shape',
    detail: `${Object.keys(parsed).length} keys, recognised=${recognised.sort().join('|') || 'none'}`
  };
};

/**
 * Shown in place of a ```chart fence whose payload cannot be rendered.
 *
 * Assistant messages arrive whole — there is no text streaming on this path —
 * so an unrenderable fence is terminal, never a transient mid-stream state.
 * Surfacing the failure keeps the degraded path visible instead of leaving a
 * permanent "generating" placeholder in an otherwise complete response.
 *
 * The rejection is also logged, since a visible notice alone tells us nothing
 * about how often this fires in production — the original defect was invisible
 * for exactly that reason. Logged from an effect so it fires once per mounted
 * notice rather than on every re-render.
 *
 * @param {Object} props
 * @param {string} [props.payload] Raw fence body, for the diagnostic log only
 */
const ChartRenderFailureNotice = ({ payload }) => {
  useEffect(() => {
    const { reason, detail } = describeChartRejection(payload);
    console.warn(
      '[chart] fence could not be rendered (%s): %s (%d chars)',
      reason,
      detail,
      (payload || '').length
    );
  }, [payload]);

  return (
    <div className="alert-surface alert-surface--warning" role="alert">
      <div className="alert-surface__icon">⚠️</div>
      <div className="text-sm">
        Chart could not be rendered — unsupported chart data format.
      </div>
    </div>
  );
};

const tryParseRenderableChartData = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const candidate =
      (parsed.graph && typeof parsed.graph === 'object' && !Array.isArray(parsed.graph) ? parsed.graph : null)
      || (parsed.chartSpec && typeof parsed.chartSpec === 'object' && !Array.isArray(parsed.chartSpec) ? parsed.chartSpec : null)
      || (parsed.chart_spec && typeof parsed.chart_spec === 'object' && !Array.isArray(parsed.chart_spec) ? parsed.chart_spec : null)
      || parsed;

    if (typeof candidate.chart_id === 'string' && candidate.chart_id.trim()) {
      return { chart_id: candidate.chart_id.trim() };
    }

    // A blank type is not a type. Both branches below require a non-blank name so
    // renderability matches the backend gatekeeper, which resolves the first
    // non-blank of `chart_type` then `type`.
    if (
      typeof candidate.chart_type === 'string'
      && candidate.chart_type.trim()
      && Array.isArray(candidate.data)
    ) {
      return candidate;
    }

    if (
      typeof candidate.type === 'string'
      && candidate.type.trim()
      && Array.isArray(candidate.data)
    ) {
      const normalized = {
        chart_type: candidate.type,
        data: candidate.data,
      };

      if (typeof candidate.title === 'string') {
        normalized.title = candidate.title;
      }

      const options = { ...(candidate.options || {}) };
      if (!options.nameField && typeof candidate.nameField === 'string') {
        options.nameField = candidate.nameField;
      }
      if (!options.valueField && typeof candidate.valueField === 'string') {
        options.valueField = candidate.valueField;
      }
      if (!options.nameField && typeof candidate.labels_field === 'string') {
        options.nameField = candidate.labels_field;
      }
      if (!options.valueField && typeof candidate.values_field === 'string') {
        options.valueField = candidate.values_field;
      }
      if (Object.keys(options).length > 0) {
        normalized.options = options;
      }

      return normalized;
    }

    // Wide-format payload: `data` carries `labels` plus a series list rather
    // than the long-format rows the factory consumes.
    const wideSeriesSpec = normalizeWideSeriesChartData(candidate);
    if (wideSeriesSpec) {
      return wideSeriesSpec;
    }
  } catch (err) {
    return null;
  }

  return null;
};

/**
 * @typedef {Object} SandboxArtifact
 * @property {string} id
 * @property {string} filename
 * @property {string} url
 * @property {string} contentType
 * @property {number} sizeBytes
 */

/**
 * Enhanced Message Renderer Component
 * Intelligently renders different content types with optimal formatting
 *
 * @param {Object} props
 * @param {string} props.content - Message text to render
 * @param {string} [props.role='assistant'] - 'user' | 'assistant'
 * @param {boolean} [props.wrap=true] - Whether to wrap in a message bubble
 * @param {Function} [props.token] - Access-token getter for authenticated sub-requests
 * @param {SandboxArtifact[]} [props.artifacts] - Downloadable files produced by sandbox execution
 * @param {string|null} [props.activeArtifactId] - ID of the currently previewed artifact
 * @param {function(string): void} [props.onArtifactSelect] - Called with artifact id when a card is clicked
 */
const EnhancedMessageRenderer = ({ content, role = 'assistant', wrap = true, token, artifacts, activeArtifactId = null, onArtifactSelect = () => {} }) => {
  const [copiedItems, setCopiedItems] = useState({});
  const { showToast } = useToast();

  // Analyze content and determine rendering strategy
  const contentAnalysis = useMemo(() => {
    if (!content) return { type: ContentTypes.MARKDOWN, sections: [], complexity: {} };

    const detection = detectContentType(content);
    const sections = extractSections(content);
    const complexity = analyzeComplexity(content);

    return {
      ...detection,
      sections,
      complexity
    };
  }, [content]);

  // Handle copy to clipboard
  const handleCopy = useCallback(async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setCopiedItems(prev => ({ ...prev, [id]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Authenticated download for sandbox artifacts. Browser-initiated clicks
  // on <a href> don't include the Authorization: Bearer header, so the
  // server-side endpoint returns 401. Instead, fetch with the token, wrap
  // the response in a blob, and trigger the download programmatically.
  //
  // The scanner emits a relative URL (e.g. ``/api/sandbox-artifacts/<id>/download``)
  // which is browser-origin-relative. In CRA dev the page origin is
  // ``localhost:3000`` but the backend runs on ``localhost:8000`` exposed
  // by Docker — the React dev-server proxy can't reach it (it thinks
  // ``localhost:8000`` is itself). Match how every other service in this
  // repo resolves the API base: runtime config / ``getApiUrl()``, else the
  // current origin for production where a single origin serves both.
  const handleArtifactDownload = useCallback(async (artifact, event) => {
    event.preventDefault();
    let blobUrl = null;
    try {
      const accessToken = typeof token === 'function' ? await token() : token;
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      let fetchUrl = artifact.url;
      if (fetchUrl.startsWith('/')) {
        // the runtime API base (``getApiUrl()``) in this repo already includes
        // the ``/api`` prefix. The scanner emits a
        // URL rooted at ``/api/...`` because the backend route is defined
        // that way. Strip the leading ``/api`` before joining so we don't
        // end up with ``/api/api/...``; if the base doesn't include ``/api``
        // (e.g. a bare origin), leave the path as-is.
        const apiBase = (getApiUrl() || 'http://localhost:8000/api')
          .replace(/\/$/, '');
        const baseIncludesApi = /\/api$/.test(apiBase);
        const suffix = baseIncludesApi && fetchUrl.startsWith('/api/')
          ? fetchUrl.slice(4)  // drop the duplicate ``/api``
          : fetchUrl;
        fetchUrl = `${apiBase}${suffix}`;
      }
      const response = await fetch(fetchUrl, { headers });
      if (!response.ok) {
        throw new Error(`Download failed: HTTP ${response.status}`);
      }
      const blob = await response.blob();
      blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = artifact.filename || 'download';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (err) {
      console.error('Artifact download failed:', err);
      showToast({
        type: 'error',
        message: `Could not download ${artifact.filename}`,
        description: err.message,
      });
    } finally {
      if (blobUrl) {
        // Defer revocation so the browser has a tick to start the download.
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      }
    }
  }, [token, showToast]);

  // Render based on content type
  const renderContent = useCallback(() => {
    switch (contentAnalysis.type) {
      case ContentTypes.TABLE:
        return <TableRenderer content={content} metadata={contentAnalysis.metadata} />;
      
      case ContentTypes.JSON:
        return <JSONRenderer content={content} onCopy={handleCopy} token={token} />;
      
      case ContentTypes.CODE:
        return <CodeRenderer content={content} metadata={contentAnalysis.metadata} onCopy={handleCopy} token={token} />;
      
      case ContentTypes.STRUCTURED_DATA:
        return <StructuredDataRenderer content={content} metadata={contentAnalysis.metadata} />;
      
      case ContentTypes.LIST:
        return <EnhancedListRenderer content={content} metadata={contentAnalysis.metadata} token={token} />;

      case ContentTypes.ERROR:
      case ContentTypes.WARNING:
      case ContentTypes.SUCCESS:
        return <AlertRenderer content={content} type={contentAnalysis.type} token={token} />;

      case ContentTypes.MIXED:
        return (
          <MixedContentRenderer
            content={content}
            metadata={contentAnalysis.metadata}
            sections={contentAnalysis.sections}
            onCopy={handleCopy}
            token={token}
          />
        );

      default:
        return <EnhancedMarkdownRenderer content={content} onCopy={handleCopy} copiedItems={copiedItems} token={token} />;
    }
  }, [content, contentAnalysis, handleCopy, copiedItems, token]);

  // Wrap in container with appropriate styling
  const shouldShowComplexity = wrap && contentAnalysis.complexity?.score > 3;

  return (
    <div className={`enhanced-message-renderer ${role} ${wrap ? '' : 'enhanced-message-renderer--bare'}`.trim()}>
      {shouldShowComplexity && (
        <div className="complexity-indicator">
          Complex content • {contentAnalysis.sections.length} sections
        </div>
      )}
      {wrap ? (
        <div className={`message-bubble message-bubble--${role}`}>
          <div className="message-bubble__body">
            {renderContent()}
          </div>
        </div>
      ) : (
        renderContent()
      )}
      {Array.isArray(artifacts) && artifacts.length > 0 && (
        <DocumentList
          artifacts={artifacts}
          activeId={activeArtifactId}
          onSelect={onArtifactSelect}
          onDownload={handleArtifactDownload}
        />
      )}
    </div>
  );
};

/**
 * Table Renderer Component
 */
const TableRenderer = ({ content, metadata }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [filter, setFilter] = useState('');

  const prose = useMemo(() => splitAroundTable(content), [content]);
  // THE TABLE THAT IS RENDERED IS THE ONE PARSED. Parsing the whole message read every
  // pipe line in it, so a second table's rows became rows of the first while the
  // remainder below rendered them again. A table of a shape the split does not recognise
  // (aligned, tab) leaves `prose.table` empty and is parsed from the message as before.
  const table = useMemo(() => parseTable(prose.table || content), [prose, content]);

  const sortedAndFilteredRows = useMemo(() => {
    if (!table || !table.rows) return [];

    let rows = [...table.rows];

    // Apply filter
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      rows = rows.filter(row => {
        const rowData = Array.isArray(row) ? row : row.data || [];
        return rowData.some(cell => 
          String(cell).toLowerCase().includes(lowerFilter)
        );
      });
    }

    // Apply sort
    if (sortColumn !== null) {
      rows.sort((a, b) => {
        const aData = Array.isArray(a) ? a : a.data || [];
        const bData = Array.isArray(b) ? b : b.data || [];
        const aVal = aData[sortColumn] || '';
        const bVal = bData[sortColumn] || '';

        // Handle numeric sort
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // String sort
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return rows;
  }, [table, filter, sortColumn, sortDirection]);

  const handleSort = (columnIndex) => {
    if (sortColumn === columnIndex) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnIndex);
      setSortDirection('asc');
    }
  };

  if (!table) {
    return <EnhancedMarkdownRenderer content={content} />;
  }

  return (
    <div className="table-renderer">
      {/*
        LOSSLESS — 2026-10-08. This rendered the parsed table and nothing else, so every
        sentence around it was deleted from the screen. Measured on the stored dwell
        answer: the lead line and the footnote naming what was excluded both vanished,
        leaving columns of numbers with no statement of what they were taken over. That
        reads exactly as reported — "less readable than prose" — because the prose was
        the part that had gone.
      */}
      {prose.before && (
        <div className="table-renderer__lead">
          <EnhancedMarkdownRenderer content={prose.before} />
        </div>
      )}
      <div className="table-controls">
        <input
          type="text"
          placeholder="Filter table..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="table-filter-input"
        />
        <span className="table-filter-meta">
          {sortedAndFilteredRows.length} rows
          {filter && ` (filtered from ${table.rows.length})`}
        </span>
      </div>
      
      <div
        className="table-scroll-container"
        style={{ maxHeight: '360px' }}
      >
        <table className="enhanced-table">
          {table.headers && (
            <thead className="enhanced-table__head">
              <tr>
                {table.headers.map((header, i) => (
                  <th
                    key={i}
                    onClick={() => handleSort(i)}
                    className="enhanced-table__header"
                  >
                    <div className="enhanced-table__header-inner">
                      <span>{header}</span>
                      {sortColumn === i && (
                        <span className="enhanced-table__sort" aria-hidden="true">
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="enhanced-table__body">
            {sortedAndFilteredRows.map((row, rowIndex) => {
              const rowData = Array.isArray(row) ? row : row.data || [];
              const highlightClass = ROW_HIGHLIGHT_CLASSES[row.highlight] || '';
              
              return (
                <tr 
                  key={rowIndex}
                  className={`enhanced-table__row ${highlightClass} ${rowIndex % 2 === 0 ? 'enhanced-table__row--even' : 'enhanced-table__row--odd'}`}
                >
                  {rowData.map((cell, cellIndex) => {
                    const cellValue = formatCellContent(cell, table.columnTypes?.[cellIndex]);
                    const isIdentifier = /[0-9]/.test(cell ?? '') && /^[A-Za-z0-9._`-]+$/.test(cell ?? '');
                    const isNumericColumn = ['number', 'currency', 'percentage'].includes(table.columnTypes?.[cellIndex]);
                    const useMono = isIdentifier || isNumericColumn;

                    return (
                      <td
                        key={cellIndex}
                        className={`enhanced-table__cell ${useMono ? 'enhanced-table__cell--mono' : ''}`}
                        style={useMono ? { fontVariantNumeric: 'tabular-nums' } : undefined}
                      >
                        {cellValue}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {sortedAndFilteredRows.length === 0 && filter && (
        <div className="table-empty-state">
          No matching rows found
        </div>
      )}

      {/* The footnote. On an answer from this product it is usually the qualification. */}
      {prose.after && (
        <div className="table-renderer__note">
          <EnhancedMarkdownRenderer content={prose.after} />
        </div>
      )}
    </div>
  );
};

/**
 * JSON Renderer Component
 */
const JSONRenderer = ({ content, onCopy, token }) => {
  const jsonData = useMemo(() => {
    try {
      const trimmed = content.trim();
      if (trimmed.startsWith('```json')) {
        const jsonStr = trimmed.replace(/```json\n?/, '').replace(/```$/, '');
        return JSON.parse(jsonStr);
      }
      return JSON.parse(trimmed);
    } catch (err) {
      return null;
    }
  }, [content]);

  if (!jsonData) {
    return <EnhancedMarkdownRenderer content={content} onCopy={onCopy} token={token} />;
  }

  const chartData = tryParseRenderableChartData(JSON.stringify(jsonData));
  if (chartData) {
    return (
      <div className="responsive-chart-wrapper">
        <VChartWrapper chartData={chartData} token={token} />
      </div>
    );
  }

  return (
    <div className="json-renderer">
      <pre className="json-renderer__code">
        <code>{JSON.stringify(jsonData, null, 2)}</code>
      </pre>
    </div>
  );
};

/**
 * Code Renderer Component
 */
const CodeRenderer = ({ content, metadata, onCopy, token }) => {
  const codeId = useMemo(() => Math.random().toString(36).substr(2, 9), []);

  const extractCode = useMemo(() => {
    if (content.includes('```')) {
      const match = content.match(/```(\w+)?\n?([\s\S]*?)```/);
      if (match) {
        return {
          language: match[1] || 'plaintext',
          code: match[2].trim()
        };
      }
    }
    return {
      language: metadata?.language || 'plaintext',
      code: content
    };
  }, [content, metadata]);

  // Render charts from explicit ```chart fences and from plain JSON code fences
  // that already contain a valid chart spec or stored chart_id.
  const chartData = useMemo(
    () => tryParseRenderableChartData(extractCode.code),
    [extractCode.code]
  );

  const isMarkdownContent = useMemo(() => {
    const lang = extractCode.language.toLowerCase();
    return lang === 'plaintext' || lang === 'markdown' || lang === 'md';
  }, [extractCode.language]);

  const shouldRenderAsMarkdown = useMemo(() => {
    if (!isMarkdownContent) return false;
    const markdownPatterns = [
      /^#{1,6}\s+.+$/m,
      /\*\*[^*]+\*\*/,
      /\*[^*]+\*/,
      /^\s*[-*+]\s+/m,
      /^\s*\d+\.\s+/m,
      /\[.+\]\(.+\)/,
      /^>\s+/m,
      /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/m, // GFM table separator row
    ];
    return markdownPatterns.some(pattern => pattern.test(extractCode.code));
  }, [isMarkdownContent, extractCode.code]);

  // CodeRenderer only handles a single fenced block: `extractCode` matches the
  // first fence and the branches below return early, silently dropping any
  // subsequent blocks. When the content has more than one fenced block, defer
  // to EnhancedMarkdownRenderer, whose `pre` handler renders each fence at its
  // markdown position (charts and code blocks alike).
  const fencedBlockCount = useMemo(
    () => (content.match(/```[\s\S]*?```/g) || []).length,
    [content]
  );
  if (fencedBlockCount > 1) {
    return <EnhancedMarkdownRenderer content={content} onCopy={onCopy} token={token} />;
  }

  if (extractCode.language === 'chart' || chartData) {
    // A chart-only return would drop any prose around the fence. When the
    // message carries content outside the fence, delegate to markdown — its
    // `pre` handler renders the chart in-place with the prose preserved.
    const fenceMatch = content.match(/```(\w+)?\n?[\s\S]*?```/);
    const proseOutsideFence = fenceMatch
      ? (content.slice(0, fenceMatch.index) + content.slice(fenceMatch.index + fenceMatch[0].length)).trim()
      : '';
    if (proseOutsideFence) {
      return <EnhancedMarkdownRenderer content={content} onCopy={onCopy} token={token} />;
    }
    if (chartData) {
      return (
        <div className="responsive-chart-wrapper">
          <VChartWrapper chartData={chartData} token={token} />
        </div>
      );
    }
    return <ChartRenderFailureNotice payload={extractCode.code} />;
  }

  // If it's markdown content in a plaintext fence, render it as markdown
  if (shouldRenderAsMarkdown) {
    return <EnhancedMarkdownRenderer content={extractCode.code} onCopy={onCopy} />;
  }

  // Detect ASCII box tables inside code fences and render as HTML tables
  const hasBoxTable = /\+[-=+\s]+\+/.test(extractCode.code) && /\|.*\|/.test(extractCode.code);
  if (hasBoxTable) {
    const table = parseTable(extractCode.code);
    if (table && table.headers && table.rows.length > 0) {
      return <TableRenderer content={extractCode.code} metadata={{ tableType: 'box' }} />;
    }
  }

  // Otherwise render as a code block
  return (
    <div className="code-renderer">
      <div className="code-renderer__meta">
        <span>{extractCode.language}</span>
        <button
          onClick={() => onCopy(extractCode.code, codeId)}
          className="code-renderer__copy"
        >
          📋 Copy
        </button>
      </div>
      <pre className="code-renderer__body overflow-x-auto">
        <code className={`language-${extractCode.language}`}>
          {extractCode.code}
        </code>
      </pre>
    </div>
  );
};

/**
 * Structured Data Renderer
 */
const StructuredDataRenderer = ({ content, metadata }) => {
  const structuredData = useMemo(() => {
    const lines = content.split('\n').filter(l => l.trim());
    const data = {};

    for (const line of lines) {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      if (!match) {
        // The <dl> below renders ONLY key:value lines, so any other line
        // would be silently deleted. Signal fall-through to markdown instead.
        return null;
      }
      const [, key, value] = match;
      data[key.trim()] = value.trim();
    }

    return Object.entries(data);
  }, [content]);

  if (!structuredData) {
    return <EnhancedMarkdownRenderer content={content} />;
  }

  return (
    <div className="structured-data-renderer">
      <dl className="structured-data">
        {structuredData.map(([key, value], index) => (
          <div key={index} className="structured-data__row">
            <dt className="structured-data__key">{key}</dt>
            <dd className="structured-data__value">
              {formatValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/**
 * Enhanced List Renderer
 */
const EnhancedListRenderer = ({ content, metadata, token }) => {
  return (
    <div className="enhanced-list-renderer">
      <EnhancedMarkdownRenderer content={content} token={token} />
    </div>
  );
};

/**
 * Alert Renderer
 */
const AlertRenderer = ({ content, type, token }) => {
  const alertStyles = {
    ERROR: 'alert-surface alert-surface--danger',
    WARNING: 'alert-surface alert-surface--warning',
    SUCCESS: 'alert-surface alert-surface--success',
    INFO: 'alert-surface'
  };

  const alertIcons = {
    ERROR: '❌',
    WARNING: '⚠️',
    SUCCESS: '✅',
    INFO: 'ℹ️'
  };

  return (
    <div className={`alert-renderer ${alertStyles[type] || alertStyles.INFO}`}>
      <div className="alert-renderer__icon">{alertIcons[type] || alertIcons.INFO}</div>
      <div className="alert-renderer__body">
        <EnhancedMarkdownRenderer content={content} token={token} />
      </div>
    </div>
  );
};

/**
 * Mixed Content Renderer
 */
const MixedContentRenderer = ({ content, metadata, sections, onCopy, token }) => {
  if (sections && sections.length > 0) {
    return (
      <div className="mixed-content-renderer">
        {sections.map((section, index) => {
          const sectionId = `section-${index}`;

          return (
            <div key={sectionId} className="mixed-content-section">
              {section.title && (
                <h3 className="markdown-heading markdown-heading--3">{section.title}</h3>
              )}
              <div className="mixed-content-section__body">
                <EnhancedMessageRenderer content={section.content} wrap={false} token={token} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return <EnhancedMarkdownRenderer content={content} onCopy={onCopy} token={token} />;
};

/**
 * Enhanced Markdown Renderer
 */
const EnhancedMarkdownRenderer = ({ content, onCopy, copiedItems = {}, token }) => {
  const components = useMemo(() => ({
    // Enhanced table rendering
    table: ({ children }) => (
      <div className="table-scroll-container" style={{ maxHeight: '360px' }}>
        <table className="enhanced-table">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="enhanced-table__head">{children}</thead>,
    tbody: ({ children }) => <tbody className="enhanced-table__body">{children}</tbody>,
    th: ({ children }) => <th className="enhanced-table__header">{children}</th>,
    td: ({ children }) => <td className="enhanced-table__cell">{children}</td>,
    
    // Block code: react-markdown v10 routes fenced code blocks through `pre`
    pre: ({ children, ...props }) => {
      // Use a stable ID derived from content to support "Copied" feedback across re-renders
      const extractText = (node) => {
        if (typeof node === 'string') return node;
        if (typeof node === 'number') return String(node);
        if (!node) return '';
        if (Array.isArray(node)) return node.map(extractText).join('');
        if (React.isValidElement(node)) return extractText(node.props.children);
        return '';
      };
      const codeContent = extractText(children);
      const codeId = 'pre-' + codeContent.slice(0, 50).replace(/\W/g, '').slice(0, 20) + '-' + codeContent.length;

      // Find the code-like child (custom component or native <code>)
      const codeChild = React.Children.toArray(children).find(
        (child) => React.isValidElement(child) &&
          (child.type === 'code' || child.props?.className?.includes?.('language-') || child.props?.className?.includes?.('inline-code'))
      );
      const codeClassName = codeChild?.props?.className || '';
      const langMatch = /language-(\w+)/.exec(codeClassName);

      // Render charts from explicit ```chart fences and from plain JSON fences
      // that already contain a valid chart spec or stored chart_id.
      const inlineChartData = tryParseRenderableChartData(codeContent);
      if ((langMatch && langMatch[1] === 'chart') || inlineChartData) {
        if (inlineChartData) {
          return (
            <div className="responsive-chart-wrapper">
              <VChartWrapper chartData={inlineChartData} token={token} />
            </div>
          );
        }
        return <ChartRenderFailureNotice payload={codeContent} />;
      }

      // Check if this code block contains an ASCII box table
      // Always check regardless of language specifier — an ASCII table is never code
      const hasBoxTable = /\+[-=+\s]+\+/.test(codeContent) && /\|.*\|/.test(codeContent);
      if (hasBoxTable) {
        const table = parseTable(codeContent);
        if (table && table.headers && table.rows.length > 0) {
          return (
            <div
              className="table-scroll-container overflow-auto"
              style={{ maxHeight: '360px' }}
            >
              <table className="enhanced-table">
                <thead className="enhanced-table__head sticky top-0">
                  <tr>
                    {table.headers.map((header, i) => (
                      <th key={i} className="enhanced-table__header">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="enhanced-table__body">
                  {table.rows.map((row, rowIndex) => {
                    const cells = Array.isArray(row) ? row : row.data || [];
                    return (
                      <tr key={rowIndex} className={`enhanced-table__row ${rowIndex % 2 === 0 ? 'enhanced-table__row--even' : 'enhanced-table__row--odd'}`}>
                        {cells.map((cell, cellIndex) => (
                          <td key={cellIndex} className="enhanced-table__cell enhanced-table__cell--mono whitespace-nowrap">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
      }

      // Standard code block rendering
      return (
        <div className="relative group my-3">
          <pre className="code-renderer__body overflow-x-auto">
            {children}
          </pre>
          <button
            onClick={() => onCopy && onCopy(codeContent, codeId)}
            className={`code-renderer__copy ${copiedItems[codeId] ? 'is-active' : ''}`}
          >
            {copiedItems[codeId] ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      );
    },

    // Inline code only (react-markdown v10 sends block code through `pre`)
    code: ({ children, ...props }) => {
      return (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    },
    
    // Enhanced paragraphs with smart highlighting
    p: ({ children }) => {
      const text = String(children);
      
      // Check for special patterns - Better contrast
      if (/^(error|failed|failure):/i.test(text)) {
        return (
          <p className="markdown-callout markdown-callout--danger">
            {children}
          </p>
        );
      }
      
      if (/^(warning|caution|alert):/i.test(text)) {
        return (
          <p className="markdown-callout markdown-callout--warning">
            {children}
          </p>
        );
      }
      
      if (/^(success|completed|done):/i.test(text)) {
        return (
          <p className="markdown-callout markdown-callout--success">
            {children}
          </p>
        );
      }
      
      if (/^(note|info|tip):/i.test(text)) {
        return (
          <p className="markdown-callout">
            {children}
          </p>
        );
      }
      
      return <p className="markdown-paragraph">{children}</p>;
    },
    
    // Enhanced lists
    ul: ({ children }) => <ul className="markdown-list markdown-list--unordered">{children}</ul>,
    ol: ({ children }) => <ol className="markdown-list markdown-list--ordered">{children}</ol>,
    
    li: ({ children }) => <li className="markdown-list__item">{children}</li>,
    
    // Enhanced blockquotes
    blockquote: ({ children }) => <blockquote className="markdown-blockquote">{children}</blockquote>,
    
    // Enhanced headings
    h1: ({ children }) => <h1 className="markdown-heading markdown-heading--1">{children}</h1>,
    h2: ({ children }) => <h2 className="markdown-heading markdown-heading--2">{children}</h2>,
    h3: ({ children }) => <h3 className="markdown-heading markdown-heading--3">{children}</h3>,
    
    // Enhanced links
    a: ({ children, href }) => (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="markdown-link"
      >
        {children}
      </a>
    ),

    // Horizontal rules
    hr: () => <hr className="markdown-divider" />,
    
    // Enhanced emphasis
    strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
    em: ({ children }) => <em className="markdown-em">{children}</em>
  }), [onCopy, copiedItems, token]);

  // Pre-process content for better rendering
  const processedContent = useMemo(() => {
    if (!content) return content;

    let processed = content;

    // Convert ASCII box tables to GFM — handles both code-fenced and bare tables.
    // Helper: convert a parsed table object to GFM markdown
    const toGFM = (table) => {
      const gfm = [];
      gfm.push('| ' + table.headers.join(' | ') + ' |');
      gfm.push('| ' + table.headers.map(() => '---').join(' | ') + ' |');
      table.rows.forEach(row => {
        const cells = Array.isArray(row) ? row : row.data || [];
        gfm.push('| ' + cells.join(' | ') + ' |');
      });
      return '\n' + gfm.join('\n') + '\n';
    };

    // Helper: find box-table line sequences and convert them to GFM.
    // Works line-by-line so we don't rely on non-greedy regex (which
    // stops at the header-separator border instead of the last border).
    const convertBoxTablesInText = (text) => {
      const lines = text.split('\n');
      const result = [];
      let tableLines = [];
      let inTable = false;

      const flushTable = () => {
        if (tableLines.length === 0) return;
        const tableText = tableLines.join('\n');
        const table = parseTable(tableText);
        if (table && table.headers && table.rows.length > 0) {
          result.push(toGFM(table));
        } else {
          result.push(...tableLines);
        }
        tableLines = [];
        inTable = false;
      };

      for (const line of lines) {
        const trimmed = line.trim();
        const isBorder = /^\+[-=+]+\+$/.test(trimmed);
        const isDataRow = trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2;

        if (isBorder || (inTable && isDataRow)) {
          inTable = true;
          tableLines.push(line);
        } else {
          flushTable();
          result.push(line);
        }
      }
      flushTable();
      return result.join('\n');
    };

    // Split by code fences to process each segment appropriately
    const fenceParts = processed.split(/(```[\s\S]*?```)/g);
    processed = fenceParts.map((part, i) => {
      if (i % 2 === 1) {
        // Chart fences: return completely untouched
        if (/^```chart\b/.test(part)) {
          return part;
        }
        // Code fence — unwrap and convert if it contains a box table
        const inner = part.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
        if (/\+[-=+]+\+/.test(inner) && /\|.*\|/.test(inner)) {
          const table = parseTable(inner);
          if (table && table.headers && table.rows.length > 0) {
            return toGFM(table);
          }
        }
        return part; // Not a table — keep fence intact
      }
      // Non-fence content — convert bare box tables to GFM
      return convertBoxTablesInText(part);
    }).join('');

    const enhanceLine = (line) => line
      // Protect long numeric identifiers (handling units, etc.) from auto-formatting
      .replace(/\b(\d{10,})\b/g, '`$1`')
      // Highlight important values (numbers with units)
      .replace(
        /\b(\d+\.?\d*)\s*(days?|hours?|minutes?|seconds?|ms|%|MB|GB|KB|items?|records?)\b/gi,
        '**$1 $2**'
      )
      // Highlight dates
      .replace(
        /\b(\w{3}\s+\d{1,2},?\s+\d{4}|\d{4}-\d{2}-\d{2})\b/g,
        '**$1**'
      );

    const lines = processed.split('\n');
    let insideCodeFence = false;

    const enhanced = lines.map((line) => {
      const trimmed = line.trim();

      if (trimmed.startsWith('```')) {
        insideCodeFence = !insideCodeFence;
        return line;
      }

      if (insideCodeFence || trimmed.startsWith('|')) {
        return line;
      }

      return enhanceLine(line);
    });

    return enhanced.join('\n');
  }, [content]);

  return (
    <SafeMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {processedContent}
    </SafeMarkdown>
  );
};

// Helper constants
const ROW_HIGHLIGHT_CLASSES = {
  warning: 'enhanced-table__row--warning',
  success: 'enhanced-table__row--success',
  error: 'enhanced-table__row--danger',
};

// Helper functions
const formatCellContent = (value, type) => {
  if (!value) return '-';
  // Display booleans as icons; all other types are returned as-is.
  // Numbers, currencies, percentages, and dates are intentionally NOT
  // auto-formatted — the LLM is responsible for formatting.
  if (type === 'boolean') {
    return /yes|true|✓/i.test(value) ? '✅' : '❌';
  }
  return value;
};

const formatValue = (value) => {
  // Check if it's a number
  if (/^\d+\.?\d*$/.test(value)) {
    return <span className="font-mono">{value}</span>;
  }
  
  // Check if it's a date
  if (/\d{4}-\d{2}-\d{2}/.test(value)) {
    return <span className="text-purple-600">{value}</span>;
  }
  
  // Check for status keywords
  if (/expired|critical|failed/i.test(value)) {
    return <span className="text-red-600 font-semibold">{value}</span>;
  }
  
  if (/warning|pending/i.test(value)) {
    return <span className="text-yellow-600 font-semibold">{value}</span>;
  }
  
  if (/success|active|completed/i.test(value)) {
    return <span className="text-green-600 font-semibold">{value}</span>;
  }
  
  return value;
};

export default EnhancedMessageRenderer;
